import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../services/api';
import { useTheme } from '../App';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';

const IdeaDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { darkMode } = useTheme();
  const { user, toggleBookmark, toggleLike, toggleDislike, viewIdea, canViewIdeaDetails } = useUser();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showGoToMarketModal, setShowGoToMarketModal] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [hasViewedIdea, setHasViewedIdea] = useState(false);
  const [riskData, setRiskData] = useState(null);
  const [riskLoading, setRiskLoading] = useState(true);
  const [flippedCards, setFlippedCards] = useState({});
  const [showMitigationModal, setShowMitigationModal] = useState(false);
  const [selectedCategoryRisks, setSelectedCategoryRisks] = useState(null);

  // Add slideIn animation CSS
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        0% {
          transform: translateX(-100px);
          opacity: 0;
        }
        50% {
          opacity: 0.8;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch idea from API
  useEffect(() => {
    const fetchIdea = async () => {
      try {
        setLoading(true);
        const response = await apiClient.getIdeaById(id, i18n.language);
        const ideaData = response.idea || response.data || response;
        
        // Transform Supabase response format to frontend format
        if (ideaData) {
          // Transform competitors from array or null to array (backwards compatibility)
          ideaData.competitors = ideaData.idea_competitors || ideaData.competitors || [];
          
          // Transform financial data from array to object
          ideaData.financialData = ideaData.idea_financial_data?.[0] || ideaData.financialData || null;
          
          // Transform technical requirements from array to object  
          ideaData.technicalRequirements = ideaData.idea_technical_requirements?.[0] || ideaData.technicalRequirements || null;
        }
        
        setIdea(ideaData);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch idea:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIdea();
    }
  }, [id, i18n.language]); // Re-fetch when language changes

  // Fetch risk data for the idea
  useEffect(() => {
    const fetchRiskData = async () => {
      try {
        setRiskLoading(true);
        const risks = await apiClient.getIdeaRisks(id, i18n.language);
        setRiskData(risks);
      } catch (err) {
        console.error('Failed to fetch risk data:', err);
        setRiskData({});
      } finally {
        setRiskLoading(false);
      }
    };

    if (id) {
      fetchRiskData();
    }
  }, [id, i18n.language]);

  useEffect(() => {
    // Track views for analytics and increment view count for trial users when they access detailed analysis
    if (idea && !hasViewedIdea && user) {
      // Only count as a "view" for trial users if they can actually see the detailed content
      if (user.tier === 'pro' || user.tier === 'idealizer' || canViewIdeaDetails()) {
        viewIdea(idea.id);
        setHasViewedIdea(true);
      }
    }
  }, [idea, hasViewedIdea, user, viewIdea, canViewIdeaDetails]);

  const ideaId = parseInt(id);
  const isBookmarked = user?.bookmarkedIdeas?.includes(ideaId) || false;
  const isLiked = user?.likedIdeas?.includes(ideaId) || false;
  const isDisliked = user?.dislikedIdeas?.includes(ideaId) || false;

  // Tooltip definitions
  const tooltips = {
    arr: "Annual Recurring Revenue (ARR) - The predictable revenue that a company expects to receive annually from its subscription customers",
    tam: "Total Addressable Market (TAM) - The total market demand for a product or service, representing the revenue opportunity available if 100% market share was achieved",
    sam: "Serviceable Addressable Market (SAM) - The portion of TAM that a company can realistically target and serve with its current business model and capabilities"
  };

  // Helper function to get color based on value and type
  const getValueColor = (value, type) => {
    if (!value) return 'text-gray-600';
    const lowerValue = value.toLowerCase();
    
    switch (type) {
      case 'competition':
        if (lowerValue.includes('high')) return darkMode ? 'text-red-400' : 'text-red-600';
        if (lowerValue.includes('medium')) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
        if (lowerValue.includes('low')) return darkMode ? 'text-green-400' : 'text-green-600';
        return darkMode ? 'text-gray-300' : 'text-gray-600';
      
      case 'risk':
        if (lowerValue.includes('high')) return darkMode ? 'text-red-400' : 'text-red-600';
        if (lowerValue.includes('medium')) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
        if (lowerValue.includes('low')) return darkMode ? 'text-green-400' : 'text-green-600';
        return darkMode ? 'text-gray-300' : 'text-gray-600';
      
      case 'timing':
        if (lowerValue.includes('perfect') || lowerValue.includes('excellent')) return darkMode ? 'text-green-400' : 'text-green-600';
        if (lowerValue.includes('good')) return darkMode ? 'text-blue-400' : 'text-blue-600';
        if (lowerValue.includes('poor') || lowerValue.includes('bad')) return darkMode ? 'text-red-400' : 'text-red-600';
        return darkMode ? 'text-purple-400' : 'text-purple-600';
      
      case 'revenue':
        if (lowerValue.includes('subscription')) return darkMode ? 'text-blue-400' : 'text-blue-600';
        if (lowerValue.includes('one-time')) return darkMode ? 'text-green-400' : 'text-green-600';
        if (lowerValue.includes('freemium')) return darkMode ? 'text-purple-400' : 'text-purple-600';
        if (lowerValue.includes('advertising')) return darkMode ? 'text-orange-400' : 'text-orange-600';
        return darkMode ? 'text-gray-300' : 'text-gray-600';
      
      default:
        return darkMode ? 'text-gray-300' : 'text-gray-600';
    }
  };

  // Helper function to get icon based on type
  const getIcon = (type) => {
    switch (type) {
      case 'competition':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'risk':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'timing':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'revenue':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Helper function to translate execution difficulty
  const translateExecutionDifficulty = (difficulty) => {
    if (!difficulty) return i18n.language === 'ar' ? 'غير محدد' : 'Not specified';
    
    const difficultyMap = {
      'Easy': t('filterOptions.executionDifficulty.Easy'),
      'Moderate': t('filterOptions.executionDifficulty.Moderate'), 
      'Complex': t('filterOptions.executionDifficulty.Complex')
    };
    
    return difficultyMap[difficulty] || difficulty;
  };

  // Helper function to translate metric values
  const translateMetricValue = (value, type) => {
    if (!value || i18n.language !== 'ar') return value;
    
    const translations = {
      competition: {
        'No competition': 'لا توجد منافسة',
        'Low competition': 'منافسة منخفضة',
        'Medium competition': 'منافسة متوسطة',
        'High competition': 'منافسة عالية',
        'High Competition': 'منافسة عالية'
      },
      risk: {
        'No risk': 'لا توجد مخاطر',
        'Low risk': 'مخاطر منخفضة',
        'Medium risk': 'مخاطر متوسطة',
        'High risk': 'مخاطر عالية'
      },
      timing: {
        'Perfect timing': 'توقيت مثالي',
        'Excellent timing': 'توقيت ممتاز',
        'Good timing': 'توقيت جيد',
        'Moderate timing': 'توقيت متوسط',
        'Moderate Timing': 'توقيت متوسط',
        'Late timing': 'توقيت متأخر',
        'Poor timing': 'توقيت ضعيف',
        'Bad timing': 'توقيت سيء'
      },
      revenue: {
        'Subscription': 'اشتراك',
        'One-time': 'دفعة واحدة',
        'Freemium': 'مجاني مع ميزات مدفوعة',
        'Advertising': 'إعلانات',
        'Commission': 'عمولة',
        'Marketplace': 'سوق إلكتروني'
      }
    };
    
    return translations[type]?.[value] || value;
  };

  // Helper function to translate type and industry values
  const translateTypeOrIndustry = (value, type) => {
    if (!value || i18n.language !== 'ar') return value;
    
    const translations = {
      type: {
        'SaaS': 'برمجيات الخدمة',
        'Mobile app': 'تطبيق محمول',
        'E-commerce website': 'موقع تجارة إلكترونية',
        'Platform': 'منصة',
        'Web app': 'تطبيق ويب',
        'Website': 'موقع إلكتروني',
        'Marketplace': 'سوق إلكتروني'
      },
      industry: {
        'Education': 'التعليم',
        'Entertainment': 'الترفيه',
        'Sports': 'الرياضة',
        'Shopping': 'التسوق',
        'Social Media': 'وسائل التواصل الاجتماعي',
        'Self-Development': 'التطوير الذاتي',
        'Productivity': 'الإنتاجية',
        'E-commerce': 'التجارة الإلكترونية',
        'Services': 'الخدمات',
        'Fashion': 'الأزياء',
        'Healthcare': 'الرعاية الصحية',
        'Finance': 'المالية',
        'Travel': 'السفر',
        'Food': 'الطعام',
        'Technology': 'التكنولوجيا'
      }
    };
    
    return translations[type]?.[value] || value;
  };

  if (!idea) {
    return (
      <div className="fade-in-soft">
        <div className="card-soft text-center py-16">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-3xl font-bold text-slate-700 mb-4">{t('ideaDetail.ideaNotFound')}</h2>
          <p className="text-slate-500 mb-6">{t('ideaDetail.ideaNotFoundDescription')}</p>
          <Link to="/ideas-vault" className="btn-soft btn-soft-primary">
            {t('ideaDetail.backToIdeasVault')}
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="fade-in-soft">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-slate-600'}>Loading idea details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fade-in-soft">
        <div className={`p-6 rounded-2xl shadow-soft-lg text-center ${
          darkMode ? 'bg-red-900 bg-opacity-20 border border-red-500' : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`mb-4 ${
            darkMode ? 'text-red-300' : 'text-red-600'
          }`}>
            Error loading idea: {error}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-soft btn-soft-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No idea found state
  if (!idea) {
    return (
      <div className="fade-in-soft">
        <div className="text-center py-16">
          <p className={darkMode ? 'text-gray-300' : 'text-slate-600'}>Idea not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in-soft">
      {/* Hero Section */}
      <div className="mb-6">
        {/* Show only title if user is out of views */}
        {user && user.tier === 'free' && !canViewIdeaDetails() ? (
          <div className="mb-4">
            <h1 className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-slate-700'
            }`}>{idea.title}</h1>
          </div>
        ) : (
          <>
            {/* Views Counter for Trial Users */}
        {user && user.tier === 'free' && (
          <div className="mb-4 flex justify-end">
            <div className={`px-4 py-2 rounded-lg border ${
              (user.ideaViews || 0) < 3 
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-red-50 border-red-200 text-red-700'
            } ${
              darkMode && (user.ideaViews || 0) < 3 
                ? 'bg-blue-900/20 border-blue-800 text-blue-300'
                : darkMode && 'bg-red-900/20 border-red-800 text-red-300'
            }`}>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">
                  {user.ideaViews || 0}/3 {i18n.language === 'ar' ? 'مشاهدات مفصلة' : 'detailed views'}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Header with title and interaction buttons */}
        <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {translateTypeOrIndustry(idea.type, 'type')}
              </div>
              <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                {translateTypeOrIndustry(idea.industry, 'industry')}
              </div>
              <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-medium">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {translateExecutionDifficulty(idea.execution_difficulty)}
              </div>
            </div>
            
            <div className="mb-4">
              <h1 className={`text-2xl font-bold ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>{idea.title}</h1>
            </div>
          </div>

          {/* Interaction buttons */}
          <div className={`flex items-center gap-2 ${isRTL ? 'order-first ml-4' : 'ml-4'}`}>
            <button
              onClick={async () => {
                if (!user) return;
                const response = await toggleLike(ideaId);
                if (response && (response.likesCount !== undefined || response.dislikesCount !== undefined)) {
                  setIdea(prev => ({
                    ...prev,
                    likes_count: response.likesCount ?? prev?.likes_count ?? 0,
                    dislikes_count: response.dislikesCount ?? prev?.dislikes_count ?? 0
                  }));
                }
              }}
              className={`flex items-center justify-center gap-1 min-w-[60px] px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                isLiked
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={isLiked ? t('ideaDetail.liked') : t('ideaDetail.like')}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7.493 18.75c-.425 0-.82-.236-.975-.632A7.48 7.48 0 016 15.375c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75 2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23h-.777zM2.331 10.977a11.969 11.969 0 00-.831 4.398 12 12 0 00.52 3.507c.26.85 1.084 1.368 1.973 1.368H4.9c.445 0 .72-.498.523-.898a8.963 8.963 0 01-.924-3.977c0-1.708.476-3.305 1.302-4.666.245-.403-.028-.959-.5-.959H4.25c-.832 0-1.612.453-1.918 1.227z" />
              </svg>
              <span className="text-sm font-semibold">{idea?.likes_count ?? 0}</span>
            </button>

            <button
              onClick={async () => {
                if (!user) return;
                const response = await toggleDislike(ideaId);
                if (response && (response.likesCount !== undefined || response.dislikesCount !== undefined)) {
                  setIdea(prev => ({
                    ...prev,
                    likes_count: response.likesCount ?? prev?.likes_count ?? 0,
                    dislikes_count: response.dislikesCount ?? prev?.dislikes_count ?? 0
                  }));
                }
              }}
              className={`flex items-center justify-center gap-1 min-w-[60px] px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                isDisliked
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={isDisliked ? t('ideaDetail.disliked') : t('ideaDetail.dislike')}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.73 5.25h1.035A7.465 7.465 0 0118 9.375a7.465 7.465 0 01-1.235 4.125h-.148c-.806 0-1.534.446-2.031 1.08a9.04 9.04 0 01-2.861 2.4c-.723.384-1.35.956-1.653 1.715a4.498 4.498 0 00-.322 1.672V21a.75.75 0 01-.75.75A2.25 2.25 0 016.75 19.5a2.25 2.25 0 01.723-1.68c.266-.559-.107-1.282-.725-1.282H3.622c-1.026 0-1.945-.694-2.054-1.715A12.134 12.134 0 011.5 12c0-2.848.992-5.464 2.649-7.521C4.537 4.027 5.136 3.75 5.754 3.75h9.776c.483 0 .964.078 1.423.23l3.114 1.04a4.5 4.5 0 001.423.23zM21.669 13.023c.536-1.362.831-2.845.831-4.398 0-1.22-.182-2.398-.52-3.507-.26-.85-1.084-1.368-1.973-1.368H19.1c-.445 0-.72.498-.523.898.591 1.2.924 2.55.924 3.977a8.959 8.959 0 01-1.302 4.666c-.245.403.028.959.5.959h1.053c.832 0 1.612-.453 1.918-1.227z" />
              </svg>
              <span className="text-sm font-semibold">{idea?.dislikes_count ?? 0}</span>
            </button>

            <button
              onClick={async () => {
                if (!user) return;
                await toggleBookmark(ideaId);
                // No need to update counts for bookmark, just the user state
              }}
              className={`flex items-center justify-center gap-1 min-w-[60px] px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                isBookmarked
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={isBookmarked ? t('ideaDetail.bookmarked') : t('ideaDetail.bookmark')}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M6.32 2.577a49.255 49.255 0 0111.36 0c1.497.174 2.57 1.46 2.57 2.93V21a.75.75 0 01-1.085.67L12 18.089l-7.165 3.583A.75.75 0 013.75 21V5.507c0-1.47 1.073-2.756 2.57-2.93z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-semibold">{isBookmarked ? '★' : '☆'}</span>
            </button>
          </div>
        </div>
        <div className="mb-4">
          <h3 className={`text-base font-semibold mb-2 ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>{t('ideaDetail.description')}</h3>
          <p className={`text-sm leading-relaxed ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>{idea.description}</p>
        </div>
        
        {/* First row: Competition, Risk, and Timing */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className={`rounded-lg p-3 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
          }`}>
            <div className={`text-xs mb-1 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className={getValueColor(idea.competition, 'competition')}>
                {getIcon('competition')}
              </span>
              {t('ideasVault.competition')}
            </div>
            <div className={`text-sm font-medium ${
              getValueColor(idea.competition, 'competition')
            }`}>{translateMetricValue(idea.competition, 'competition')}</div>
          </div>
          <div className={`rounded-lg p-3 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
          }`}>
            <div className={`text-xs mb-1 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className={getValueColor(idea.regulatory_risk, 'risk')}>
                {getIcon('risk')}
              </span>
              {t('ideasVault.risk')}
            </div>
            <div className={`text-sm font-medium ${
              getValueColor(idea.regulatory_risk, 'risk')
            }`}>{translateMetricValue(idea.regulatory_risk, 'risk')}</div>
          </div>
          <div className={`rounded-lg p-3 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
          }`}>
            <div className={`text-xs mb-1 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className={getValueColor(idea.timing, 'timing')}>
                {getIcon('timing')}
              </span>
              {t('ideasVault.timing')}
            </div>
            <div className={`text-sm font-medium ${
              getValueColor(idea.timing, 'timing')
            }`}>{translateMetricValue(idea.timing, 'timing')}</div>
          </div>
        </div>
        
        {/* Second row: Revenue Model */}
        <div className="grid grid-cols-1 gap-3 mb-6">
          <div className={`rounded-lg p-3 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
          }`}>
            <div className={`text-xs mb-1 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className="text-blue-500">
                {getIcon('revenue')}
              </span>
              {t('ideasVault.revenue')}
            </div>
            <div className={`text-sm font-medium ${
              getValueColor(idea.revenue_model || 'Subscription', 'revenue')
            }`}>{translateMetricValue(idea.revenue_model || 'Subscription', 'revenue')}</div>
          </div>
        </div>
          </>
        )}

      </div>

      {/* Out of Views Banner */}
      {user && user.tier === 'free' && !canViewIdeaDetails() && (
        <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold mb-1">
                {i18n.language === 'ar' ? 'لا يوجد مشاهدات كافية' : 'Out of Views!'}
              </h3>
              <p className="text-sm opacity-90">
                {i18n.language === 'ar' 
                  ? 'لقد استنفدت جميع مشاهداتك للتحليل المفصل. يرجى الاشتراك للاستمرار في مشاهدة تفاصيل الأفكار.'
                  : 'You have used all your detailed view allowance. Please subscribe to continue viewing full idea analysis.'
                }
              </p>
            </div>
            <button
              className="px-4 py-2 bg-white text-red-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              onClick={() => {
                navigate('/profile#billing');
              }}
            >
              {i18n.language === 'ar' ? 'ترقية الآن' : 'Upgrade Now'}
            </button>
          </div>
        </div>
      )}

      {/* Conditional Content - Only show detailed analysis if user can view */}
      {(!user || user.tier === 'pro' || user.tier === 'idealizer' || canViewIdeaDetails()) ? (
        <>
          {/* Section C: Why Now? */}
      <div className="mb-8">
        <h2 className={`text-xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>{t('ideaOfTheDay.whyNow')}</h2>
        <div className={`rounded-lg p-4 ${
          darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
        }`}>
          <p className={`text-sm ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>{idea.why_now || idea.whyNow || 'No information available'}</p>
        </div>
      </div>

      {/* Key Insights Section */}
      <div className="mb-8">
        <h2 className={`text-xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>{t('ideaDetail.keyInsights')}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Financial Potential Button */}
          <div 
            className={`group relative flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-xl p-6 transition-all duration-500 hover:scale-[1.02] ${
              darkMode 
                ? 'bg-gradient-to-br from-emerald-900/30 via-emerald-800/20 to-teal-900/30 border border-emerald-500/20 hover:border-emerald-400/40' 
                : 'bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border border-emerald-200/50 hover:border-emerald-300/60'
            }`}
            onClick={() => setShowFinancialModal(true)}
          >
            {/* Grid Pattern Background */}
            <div className={`absolute -right-1/2 top-0 z-0 size-full cursor-pointer ${
              darkMode 
                ? 'bg-[linear-gradient(to_right,#065f4630_1px,transparent_1px),linear-gradient(to_bottom,#065f4630_1px,transparent_1px)]' 
                : 'bg-[linear-gradient(to_right,#065f4620_1px,transparent_1px),linear-gradient(to_bottom,#065f4620_1px,transparent_1px)]'
            } bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]`}></div>
            
            {/* Large Background Icon */}
            <div className={`absolute bottom-3 right-1 scale-[6] transition-all duration-700 group-hover:scale-[6.2] ${
              darkMode ? 'text-emerald-500/5 group-hover:text-emerald-400/10' : 'text-emerald-500/5 group-hover:text-emerald-500/10'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full shadow transition-all duration-500 ${
                  darkMode 
                    ? 'bg-emerald-500/20 text-emerald-300 shadow-emerald-500/20 group-hover:bg-emerald-500/30 group-hover:shadow-emerald-400/30' 
                    : 'bg-emerald-100 text-emerald-600 shadow-emerald-500/20 group-hover:bg-emerald-200 group-hover:shadow-emerald-500/30'
                }`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className={`mb-2 text-xl font-semibold tracking-tight ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaDetail.financialPotential')}</h3>
                <p className={`text-sm ${
                  darkMode ? 'text-emerald-100' : 'text-emerald-700'
                } text-start`}>{t('ideaDetail.viewRevenueProjec')}</p>
              </div>
              <div className={`mt-4 flex items-center text-sm transition-all duration-500 group-hover:translate-x-1 ${
                darkMode ? 'text-emerald-300' : 'text-emerald-600'
              }`}>
                <span className={isRTL ? 'ml-2' : 'mr-2'}>{t('ideaDetail.learnMore')}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-1 w-full blur-2xl transition-all duration-500 group-hover:blur-lg ${
              darkMode ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : 'bg-gradient-to-r from-emerald-500 to-teal-500'
            }`} />
          </div>

          {/* Execution Button */}
          <div 
            className={`group relative flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-xl p-6 transition-all duration-500 hover:scale-[1.02] ${
              darkMode 
                ? 'bg-gradient-to-br from-blue-900/30 via-indigo-800/20 to-purple-900/30 border border-blue-500/20 hover:border-blue-400/40' 
                : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-200/50 hover:border-blue-300/60'
            }`}
            onClick={() => setShowExecutionModal(true)}
          >
            {/* Grid Pattern Background */}
            <div className={`absolute -right-1/2 top-0 z-0 size-full cursor-pointer ${
              darkMode 
                ? 'bg-[linear-gradient(to_right,#1e40af30_1px,transparent_1px),linear-gradient(to_bottom,#1e40af30_1px,transparent_1px)]' 
                : 'bg-[linear-gradient(to_right,#1e40af20_1px,transparent_1px),linear-gradient(to_bottom,#1e40af20_1px,transparent_1px)]'
            } bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]`}></div>
            
            {/* Large Background Icon */}
            <div className={`absolute bottom-3 right-1 scale-[6] transition-all duration-700 group-hover:scale-[6.2] ${
              darkMode ? 'text-blue-500/5 group-hover:text-blue-400/10' : 'text-blue-500/5 group-hover:text-blue-500/10'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full shadow transition-all duration-500 ${
                  darkMode 
                    ? 'bg-blue-500/20 text-blue-300 shadow-blue-500/20 group-hover:bg-blue-500/30 group-hover:shadow-blue-400/30' 
                    : 'bg-blue-100 text-blue-600 shadow-blue-500/20 group-hover:bg-blue-200 group-hover:shadow-blue-500/30'
                }`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className={`mb-2 text-xl font-semibold tracking-tight ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaDetail.execution')}</h3>
                <p className={`text-sm ${
                  darkMode ? 'text-blue-100' : 'text-blue-700'
                } text-start`}>{t('ideaDetail.techRequirements')}</p>
              </div>
              <div className={`mt-4 flex items-center text-sm transition-all duration-500 group-hover:translate-x-1 ${
                darkMode ? 'text-blue-300' : 'text-blue-600'
              }`}>
                <span className={isRTL ? 'ml-2' : 'mr-2'}>{t('ideaDetail.learnMore')}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-1 w-full blur-2xl transition-all duration-500 group-hover:blur-lg ${
              darkMode ? 'bg-gradient-to-r from-blue-400 to-purple-400' : 'bg-gradient-to-r from-blue-500 to-purple-500'
            }`} />
          </div>

          {/* Go-to-Market Button */}
          <div 
            className={`group relative flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-xl p-6 transition-all duration-500 hover:scale-[1.02] ${
              darkMode 
                ? 'bg-gradient-to-br from-purple-900/30 via-pink-800/20 to-rose-900/30 border border-purple-500/20 hover:border-purple-400/40' 
                : 'bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 border border-purple-200/50 hover:border-purple-300/60'
            }`}
            onClick={() => setShowGoToMarketModal(true)}
          >
            {/* Grid Pattern Background */}
            <div className={`absolute -right-1/2 top-0 z-0 size-full cursor-pointer ${
              darkMode 
                ? 'bg-[linear-gradient(to_right,#7c3aed30_1px,transparent_1px),linear-gradient(to_bottom,#7c3aed30_1px,transparent_1px)]' 
                : 'bg-[linear-gradient(to_right,#7c3aed20_1px,transparent_1px),linear-gradient(to_bottom,#7c3aed20_1px,transparent_1px)]'
            } bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]`}></div>
            
            {/* Large Background Icon */}
            <div className={`absolute bottom-3 right-1 scale-[6] transition-all duration-700 group-hover:scale-[6.2] ${
              darkMode ? 'text-purple-500/5 group-hover:text-purple-400/10' : 'text-purple-500/5 group-hover:text-purple-500/10'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full shadow transition-all duration-500 ${
                  darkMode 
                    ? 'bg-purple-500/20 text-purple-300 shadow-purple-500/20 group-hover:bg-purple-500/30 group-hover:shadow-purple-400/30' 
                    : 'bg-purple-100 text-purple-600 shadow-purple-500/20 group-hover:bg-purple-200 group-hover:shadow-purple-500/30'
                }`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className={`mb-2 text-xl font-semibold tracking-tight ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaDetail.goToMarket')}</h3>
                <p className={`text-sm ${
                  darkMode ? 'text-purple-100' : 'text-purple-700'
                } text-start`}>{t('ideaDetail.marketingStrategy')}</p>
              </div>
              <div className={`mt-4 flex items-center text-sm transition-all duration-500 group-hover:translate-x-1 ${
                darkMode ? 'text-purple-300' : 'text-purple-600'
              }`}>
                <span className={isRTL ? 'ml-2' : 'mr-2'}>{t('ideaDetail.learnMore')}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-1 w-full blur-2xl transition-all duration-500 group-hover:blur-lg ${
              darkMode ? 'bg-gradient-to-r from-purple-400 to-pink-400' : 'bg-gradient-to-r from-purple-500 to-pink-500'
            }`} />
          </div>
        </div>
      </div>

      {/* Top Competitors Section */}
      <div className="mb-8">
        <h2 className={`text-xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>{t('ideaOfTheDay.topCompetitors')}</h2>
        <div className="space-y-6">
          {(idea.idea_competitors || idea.competitors) && (idea.idea_competitors || idea.competitors).length > 0 ? (idea.idea_competitors || idea.competitors).map((competitor, index) => (
            <div key={index} className={`rounded-lg p-4 ${
              darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 ${
                darkMode ? 'text-white' : 'text-slate-700'
              } text-start`}>{competitor.name}</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Strengths Box */}
                <div className={`rounded-lg p-4 border-l-4 ${
                  darkMode 
                    ? 'bg-gradient-to-br from-emerald-900/20 via-teal-900/15 to-cyan-900/20 border-emerald-500/50 border-l-emerald-400' 
                    : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-emerald-200 border-l-emerald-500'
                }`}>
                  <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                    darkMode ? 'text-emerald-300' : 'text-emerald-700'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('ideaOfTheDay.strengths')}
                  </h4>
                  <ul className={`text-sm space-y-1 ${
                    darkMode ? 'text-emerald-100' : 'text-emerald-700'
                  }`}>
                    {competitor.strengths && competitor.strengths.length > 0 ? competitor.strengths.map((strength, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={`mt-1 ${
                          darkMode ? 'text-emerald-400' : 'text-emerald-500'
                        }`}>•</span>
                        <span>{strength}</span>
                      </li>
                    )) : (
                      <li className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>No strengths listed</li>
                    )}
                  </ul>
                </div>
                
                {/* Weaknesses Box */}
                <div className={`rounded-lg p-4 border-l-4 ${
                  darkMode 
                    ? 'bg-gradient-to-br from-orange-900/20 via-amber-900/15 to-yellow-900/20 border-orange-500/50 border-l-orange-400' 
                    : 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-orange-200 border-l-orange-500'
                }`}>
                  <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                    darkMode ? 'text-orange-300' : 'text-orange-700'
                  }`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    {t('ideaOfTheDay.weaknesses')}
                  </h4>
                  <ul className={`text-sm space-y-1 ${
                    darkMode ? 'text-orange-100' : 'text-orange-700'
                  }`}>
                    {competitor.weaknesses && competitor.weaknesses.length > 0 ? competitor.weaknesses.map((weakness, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={`mt-1 ${
                          darkMode ? 'text-orange-400' : 'text-orange-500'
                        }`}>•</span>
                        <span>{weakness}</span>
                      </li>
                    )) : (
                      <li className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>No weaknesses listed</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )) : (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              No competitors data available
            </div>
          )}
        </div>
      </div>

      {/* Target Audience */}
      <div className={`p-6 mb-8 hover-soft rounded-2xl shadow-soft-lg ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <h4 className={`text-xl font-semibold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>{t('ideaOfTheDay.targetAudience')}</h4>
        <p className={`leading-relaxed ${
          darkMode ? 'text-gray-300' : 'text-slate-600'
        }`}>{idea.target_audience || idea.targetAudience}</p>
      </div>

      {/* Risk Analysis Section */}
      <div className="mb-8">
        <h2 className={`text-xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>{isRTL ? 'تحليل المخاطر' : 'Risk Analysis'}</h2>
        
        {riskLoading ? (
          <div className={`rounded-lg p-8 ${
            darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
          }`}>
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
            </div>
          </div>
        ) : (
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
          }`}>
            {riskData && Object.keys(riskData).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {['Financial', 'Market', 'Technical', 'Operational', 'Regulatory'].map((category) => {
                  const categoryRisks = riskData[category] || [];
                  const hasRisks = categoryRisks.length > 0;
                  const isFlipped = flippedCards[category] || false;
                  
                  // Risk level colors and icons - more calm/subdued tones
                  const getRiskColor = (level) => {
                    switch(level?.toLowerCase()) {
                      case 'high': return 'from-rose-400 to-rose-500';
                      case 'medium': return 'from-amber-400 to-amber-500';
                      case 'low': return 'from-emerald-400 to-emerald-500';
                      default: return 'from-slate-400 to-slate-500';
                    }
                  };

                  const getCategoryIcon = (category) => {
                    const icons = {
                      'Financial': '💰',
                      'Market': '📈',
                      'Technical': '⚙️',
                      'Operational': '🔧',
                      'Regulatory': '📋'
                    };
                    return icons[category] || '⚠️';
                  };

                  const getHighestRisk = () => {
                    if (!hasRisks) return 'none';
                    return categoryRisks.reduce((highest, risk) => {
                      const levels = { low: 1, medium: 2, high: 3 };
                      return (levels[risk.risk_level?.toLowerCase()] || 0) > (levels[highest?.toLowerCase()] || 0) 
                        ? risk.risk_level : highest;
                    }, 'low');
                  };

                  const highestRisk = getHighestRisk();

                  return (
                    <div
                      key={category}
                      className="group relative h-[360px] w-full [perspective:2000px]"
                      onMouseEnter={() => setFlippedCards(prev => ({ ...prev, [category]: true }))}
                      onMouseLeave={() => setFlippedCards(prev => ({ ...prev, [category]: false }))}
                    >
                      <div
                        className={`relative h-full w-full [transform-style:preserve-3d] transition-all duration-700 ${
                          isFlipped ? '[transform:rotateY(180deg)]' : '[transform:rotateY(0deg)]'
                        }`}
                      >
                        {/* Front of card */}
                        <div
                          className={`absolute inset-0 h-full w-full [transform:rotateY(0deg)] [backface-visibility:hidden] overflow-hidden rounded-2xl ${
                            darkMode 
                              ? 'bg-gradient-to-br from-gray-900 via-gray-900/95 to-gray-800 border border-gray-800/50' 
                              : 'bg-gradient-to-br from-white via-slate-50 to-slate-100 border border-slate-200'
                          } shadow-lg transition-all duration-700 group-hover:shadow-xl ${
                            hasRisks && highestRisk === 'high' 
                              ? 'group-hover:border-rose-500/20' 
                              : hasRisks && highestRisk === 'medium'
                              ? 'group-hover:border-amber-500/20'
                              : hasRisks && highestRisk === 'low'
                              ? 'group-hover:border-emerald-500/20'
                              : 'group-hover:border-slate-500/15'
                          } ${isFlipped ? 'opacity-0' : 'opacity-100'}`}
                        >
                          {/* Background gradient effect */}
                          <div className={`absolute inset-0 bg-gradient-to-br via-transparent ${
                            hasRisks && highestRisk === 'high' 
                              ? 'from-rose-500/3 to-rose-500/6' 
                              : hasRisks && highestRisk === 'medium'
                              ? 'from-amber-500/3 to-amber-500/6'
                              : hasRisks && highestRisk === 'low'
                              ? 'from-emerald-500/3 to-emerald-500/6'
                              : 'from-slate-500/3 to-slate-500/6'
                          }`} />

                          {/* Animated risk indicators */}
                          <div className="absolute inset-0 flex items-center justify-center pt-20">
                            <div className="relative flex h-[100px] w-[200px] flex-col items-center justify-center gap-2">
                              {/* Multiple animated bars - always show 6 lines for animation effect */}
                              {[...Array(6)].map((_, i) => (
                                <div
                                  key={i}
                                  className={`h-3 rounded-sm bg-gradient-to-r opacity-0 animate-[slideIn_4s_ease-in-out_infinite] ${
                                    hasRisks && categoryRisks[i] 
                                      ? getRiskColor(categoryRisks[i].risk_level)
                                      : hasRisks && categoryRisks.length > 0
                                        ? getRiskColor(categoryRisks[0].risk_level)
                                        : highestRisk === 'high' 
                                          ? 'from-rose-400/15 to-rose-500/15'
                                          : highestRisk === 'medium'
                                            ? 'from-amber-400/15 to-amber-500/15'
                                            : highestRisk === 'low'
                                              ? 'from-emerald-400/15 to-emerald-500/15'
                                              : 'from-slate-400/15 to-slate-500/15'
                                  }`}
                                  style={{
                                    width: `${60 + Math.random() * 40}%`,
                                    animationDelay: `${i * 0.2}s`,
                                    marginLeft: `${Math.random() * 20}%`,
                                  }}
                                />
                              ))}

                              {/* Central category icon */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div
                                  className={`h-12 w-12 rounded-xl flex items-center justify-center shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 ${
                                    hasRisks && highestRisk === 'high' 
                                      ? 'bg-gradient-to-br from-rose-400 via-rose-400/90 to-rose-400/80 shadow-rose-400/20' 
                                      : hasRisks && highestRisk === 'medium'
                                      ? 'bg-gradient-to-br from-amber-400 via-amber-400/90 to-amber-400/80 shadow-amber-400/20'
                                      : hasRisks && highestRisk === 'low'
                                      ? 'bg-gradient-to-br from-emerald-400 via-emerald-400/90 to-emerald-400/80 shadow-emerald-400/20'
                                      : 'bg-gradient-to-br from-slate-400 via-slate-400/90 to-slate-400/80 shadow-slate-400/20'
                                  } ${!hasRisks ? 'animate-pulse' : ''}`}
                                >
                                  <span className="text-white text-xl">{getCategoryIcon(category)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom content */}
                          <div className="absolute right-0 bottom-0 left-0 p-5">
                            <div className="flex items-center justify-between gap-3">
                              <div className="space-y-1.5">
                                <h3 className={`text-lg leading-snug font-semibold tracking-tight transition-all duration-500 ease-out group-hover:translate-y-[-4px] ${
                                  darkMode ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {isRTL ? {
                                    'Financial': 'مخاطر مالية',
                                    'Market': 'مخاطر السوق',
                                    'Technical': 'مخاطر تقنية',
                                    'Operational': 'مخاطر تشغيلية',
                                    'Regulatory': 'مخاطر تنظيمية'
                                  }[category] : `${category} Risks`}
                                </h3>
                                <p className={`text-sm tracking-tight transition-all delay-[50ms] duration-500 ease-out group-hover:translate-y-[-4px] ${
                                  darkMode ? 'text-gray-300' : 'text-gray-600'
                                }`}>
                                  {hasRisks ? `${categoryRisks.length} ${isRTL ? 'مخاطر محددة' : 'identified risks'}` : isRTL ? 'لا توجد مخاطر' : 'No risks identified'}
                                </p>
                              </div>
                              <div className="group/icon relative">
                                <div
                                  className={`absolute inset-[-8px] rounded-lg transition-opacity duration-300 opacity-0 group-hover/icon:opacity-100 ${
                                    hasRisks && highestRisk === 'high' 
                                      ? 'bg-gradient-to-br from-rose-400/15 via-rose-400/8' 
                                      : hasRisks && highestRisk === 'medium'
                                      ? 'bg-gradient-to-br from-amber-400/15 via-amber-400/8'
                                      : hasRisks && highestRisk === 'low'
                                      ? 'bg-gradient-to-br from-emerald-400/15 via-emerald-400/8'
                                      : 'bg-gradient-to-br from-slate-400/15 via-slate-400/8'
                                  } to-transparent`}
                                />
                                <div className={`relative z-10 h-5 w-5 rounded-full flex items-center justify-center transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:rotate-12 ${
                                  hasRisks && highestRisk === 'high' 
                                    ? 'bg-rose-400 text-white' 
                                    : hasRisks && highestRisk === 'medium'
                                    ? 'bg-amber-400 text-white'
                                    : hasRisks && highestRisk === 'low'
                                    ? 'bg-emerald-400 text-white'
                                    : 'bg-slate-400 text-white'
                                }`}>
                                  <span className="text-xs font-bold">{hasRisks ? categoryRisks.length : '✓'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Back of card */}
                        <div
                          className={`absolute inset-0 h-full w-full [transform:rotateY(180deg)] [backface-visibility:hidden] rounded-2xl p-5 flex flex-col transition-all duration-700 ${
                            darkMode 
                              ? 'bg-gradient-to-br from-gray-900 via-gray-900/95 to-gray-800 border border-gray-800' 
                              : 'bg-gradient-to-br from-white via-slate-50 to-slate-100 border border-slate-200'
                          } shadow-lg group-hover:shadow-xl ${
                            hasRisks && highestRisk === 'high' 
                              ? 'group-hover:border-rose-500/20' 
                              : hasRisks && highestRisk === 'medium'
                              ? 'group-hover:border-amber-500/20'
                              : hasRisks && highestRisk === 'low'
                              ? 'group-hover:border-emerald-500/20'
                              : 'group-hover:border-slate-500/15'
                          } ${!isFlipped ? 'opacity-0' : 'opacity-100'}`}
                        >
                          {/* Background gradient */}
                          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br via-transparent ${
                            hasRisks && highestRisk === 'high' 
                              ? 'from-rose-500/3 to-rose-500/6' 
                              : hasRisks && highestRisk === 'medium'
                              ? 'from-amber-500/3 to-amber-500/6'
                              : hasRisks && highestRisk === 'low'
                              ? 'from-emerald-500/3 to-emerald-500/6'
                              : 'from-slate-500/3 to-slate-500/6'
                          }`} />

                          <div className="relative z-10 flex-1 space-y-4">
                            <div className="space-y-2">
                              <div className="mb-2 flex items-center gap-2">
                                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                                  hasRisks && highestRisk === 'high' 
                                    ? 'bg-gradient-to-br from-rose-400 via-rose-400/90 to-rose-400/80' 
                                    : hasRisks && highestRisk === 'medium'
                                    ? 'bg-gradient-to-br from-amber-400 via-amber-400/90 to-amber-400/80'
                                    : hasRisks && highestRisk === 'low'
                                    ? 'bg-gradient-to-br from-emerald-400 via-emerald-400/90 to-emerald-400/80'
                                    : 'bg-gradient-to-br from-slate-400 via-slate-400/90 to-slate-400/80'
                                }`}>
                                  <span className="text-white text-sm">{getCategoryIcon(category)}</span>
                                </div>
                                <h3 className={`text-lg leading-snug font-semibold tracking-tight transition-all duration-500 ease-out group-hover:translate-y-[-2px] ${
                                  darkMode ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {isRTL ? {
                                    'Financial': 'مالية',
                                    'Market': 'السوق',
                                    'Technical': 'تقنية',
                                    'Operational': 'تشغيلية',
                                    'Regulatory': 'تنظيمية'
                                  }[category] : category}
                                </h3>
                              </div>
                              <p className={`text-sm tracking-tight transition-all duration-500 ease-out group-hover:translate-y-[-2px] ${
                                darkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {hasRisks ? `${categoryRisks.length} ${isRTL ? 'مخاطر محددة لهذه الفئة' : 'risks identified in this category'}` : isRTL ? 'لا توجد مخاطر محددة لهذه الفئة' : 'No risks identified for this category'}
                              </p>
                            </div>

                            {hasRisks ? (
                              <div className="space-y-2.5 max-h-[200px] overflow-y-auto">
                                {categoryRisks.slice(0, 3).map((risk, index) => (
                                  <div
                                    key={index}
                                    className={`flex items-start gap-3 text-sm transition-all duration-500 ${
                                      darkMode ? 'text-gray-300' : 'text-gray-700'
                                    }`}
                                    style={{
                                      transform: isFlipped ? 'translateX(0)' : 'translateX(-10px)',
                                      opacity: isFlipped ? 1 : 0,
                                      transitionDelay: `${index * 100 + 200}ms`,
                                    }}
                                  >
                                    <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md ${
                                      getRiskColor(risk.risk_level) === 'from-rose-400 to-rose-500' ? 'bg-rose-400/15' :
                                      getRiskColor(risk.risk_level) === 'from-amber-400 to-amber-500' ? 'bg-amber-400/15' :
                                      getRiskColor(risk.risk_level) === 'from-emerald-400 to-emerald-500' ? 'bg-emerald-400/15' :
                                      'bg-slate-400/15'
                                    }`}>
                                      <span className={`text-xs ${
                                        getRiskColor(risk.risk_level) === 'from-rose-400 to-rose-500' ? 'text-rose-500' :
                                        getRiskColor(risk.risk_level) === 'from-amber-400 to-amber-500' ? 'text-amber-500' :
                                        getRiskColor(risk.risk_level) === 'from-emerald-400 to-emerald-500' ? 'text-emerald-500' :
                                        'text-slate-500'
                                      }`}>
                                        {risk.risk_level?.toLowerCase() === 'high' ? '🔴' : 
                                         risk.risk_level?.toLowerCase() === 'medium' ? '🟡' : 
                                         risk.risk_level?.toLowerCase() === 'low' ? '🟢' : '⚪'}
                                      </span>
                                    </div>
                                    <div className="flex-1">
                                      <span className="font-medium block mb-1">
                                        {isRTL ? (risk.risk_type_ar || risk.risk_type) : risk.risk_type}
                                      </span>
                                      <p className={`text-xs leading-relaxed ${
                                        darkMode ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        {isRTL ? (risk.risk_description_ar || risk.risk_description) : risk.risk_description}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                                {categoryRisks.length > 3 && (
                                  <div className={`text-center text-xs pt-2 ${
                                    darkMode ? 'text-gray-400' : 'text-gray-500'
                                  }`}>
                                    +{categoryRisks.length - 3} {isRTL ? 'مخاطر أخرى' : 'more risks'}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-8">
                                <div className="h-12 w-12 rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
                                  <span className="text-2xl">✅</span>
                                </div>
                                <p className={`text-sm font-medium ${
                                  darkMode ? 'text-emerald-400' : 'text-emerald-600'
                                }`}>
                                  {isRTL ? 'لا توجد مخاطر' : 'All Clear'}
                                </p>
                              </div>
                            )}
                          </div>

                          {hasRisks && (
                            <div className={`relative z-10 mt-auto border-t pt-4 ${
                              darkMode ? 'border-gray-800' : 'border-slate-200'
                            }`}>
                              <div
                                onClick={() => {
                                  setSelectedCategoryRisks({ category, risks: categoryRisks });
                                  setShowMitigationModal(true);
                                }}
                                className={`group/action relative flex items-center justify-center rounded-lg p-2.5 transition-all duration-300 border border-transparent hover:scale-[1.02] hover:cursor-pointer ${
                                  darkMode 
                                    ? 'bg-gradient-to-r from-gray-800 via-gray-800 to-gray-800 hover:from-gray-700 hover:via-gray-700 hover:to-gray-700 hover:border-gray-600' 
                                    : 'bg-gradient-to-r from-slate-100 via-slate-100 to-slate-100 hover:from-slate-50 hover:via-slate-50 hover:to-slate-50 hover:border-slate-300'
                                } ${
                                  hasRisks && highestRisk === 'high' 
                                    ? 'hover:border-rose-500/20' 
                                    : hasRisks && highestRisk === 'medium'
                                    ? 'hover:border-amber-500/20'
                                    : 'hover:border-emerald-500/20'
                                }`}
                              >
                                <span className={`text-sm font-semibold transition-colors duration-300 ${
                                  darkMode ? 'text-white group-hover/action:text-gray-200' : 'text-gray-900 group-hover/action:text-gray-700'
                                }`}>
                                  {isRTL ? 'عرض استراتيجيات التخفيف' : 'View Mitigation Strategies'}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className={`w-12 h-12 mx-auto mb-4 ${
                  darkMode ? 'text-gray-400' : 'text-slate-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L4.732 14.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className={`text-lg font-semibold mb-2 ${
                  darkMode ? 'text-white' : 'text-slate-700'
                }`}>
                  {isRTL ? 'لا توجد بيانات مخاطر' : 'No Risk Data Available'}
                </h3>
                <p className={`text-sm ${
                  darkMode ? 'text-gray-400' : 'text-slate-500'
                }`}>
                  {isRTL ? 'لم يتم تحليل المخاطر لهذه الفكرة بعد' : 'Risk analysis has not been performed for this idea yet'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Call to Action */}
      <div className="text-center">
        <div className={`p-8 rounded-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white shadow-soft-md'
        }`}>
          <h3 className={`text-2xl font-bold mb-4 ${
            darkMode ? 'text-white' : 'text-slate-700'
          } text-start`}>{t('ideaDetail.readyToBuildIdea')}</h3>
          <p className={`mb-6 ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          } text-start`}>
            {t('ideaDetail.takeToBuilderStudio')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={async () => {
                console.log('🚀 BUILD BUTTON CLICKED!', { user: !!user, ideaId: idea?.id });
                
                // Record build click directly to Supabase
                if (user && idea?.id) {
                  try {
                    // Recording build click to database
                    // User authenticated for build click
                    // Processing idea interaction
                    
                    // Check Supabase auth session
                    const { data: session, error: sessionError } = await supabase.auth.getSession();
                    // Session validation completed
                    
                    // First, let's check if we can read ANY rows at all
                    const { data: allRows, error: allRowsError } = await supabase
                      .from('user_idea_interactions')
                      .select('*')
                      .limit(5);
                    
                    // Database connectivity verified
                    
                    // Check specifically for this user
                    const { data: userRows, error: userRowsError } = await supabase
                      .from('user_idea_interactions')
                      .select('*')
                      .eq('user_id', user.id);
                    
                    // User interaction history checked
                    
                    // Check if the row exists
                    const { data: existingRow, error: selectError } = await supabase
                      .from('user_idea_interactions')
                      .select('*')
                      .eq('user_id', user.id)
                      .eq('idea_id', idea.id);
                    
                    // Checking for existing interaction record
                    
                    // Try to update the existing interaction for this user and idea
                    const { data: updateData, error: updateError } = await supabase
                      .from('user_idea_interactions')
                      .update({ 
                        build_clicked_at: new Date().toISOString()
                      })
                      .eq('user_id', user.id)
                      .eq('idea_id', idea.id)
                      .select();

                    // Attempting to update existing record

                    if (updateError) {
                      console.error('❌ Update error:', updateError);
                    } else if (updateData && updateData.length > 0) {
                      // Build click count updated successfully
                    } else {
                      // No existing interaction found, create new one
                      // Creating new interaction record
                      const { data: insertData, error: insertError } = await supabase
                        .from('user_idea_interactions')
                        .insert({
                          user_id: user.id,
                          idea_id: idea.id,
                          build_clicked_at: new Date().toISOString()
                        })
                        .select();

                      console.log('🆕 INSERT ATTEMPT:', { insertData, insertError });

                      if (insertError) {
                        console.error('❌ Insert error:', insertError);
                      } else {
                        // New build click record created successfully
                      }
                    }
                  } catch (error) {
                    console.error('❌ BUILD CLICK FAILED:', error);
                  }
                } else {
                  console.error('❌ Missing user or idea:', { user: !!user, ideaId: idea?.id });
                }
                
                // Navigate to builders studio
                console.log('🔀 Navigating to builders studio...');
                navigate(`/builders-studio?title=${encodeURIComponent(idea.title)}&description=${encodeURIComponent(idea.description)}`);
              }}
              className="btn-soft btn-soft-primary flex-1 sm:flex-initial"
            >
              {t('ideaOfTheDay.buildThisIdea')}
            </button>
            <Link to="/ideas-vault" className="btn-soft btn-soft-secondary flex-1 sm:flex-initial">
              {t('ideaDetail.backToIdeasVault')}
            </Link>
          </div>
        </div>
      </div>

      {/* Financial Modal */}
      {showFinancialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowFinancialModal(false)}>
          <div className={`rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`} onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaDetail.financialPotential')}</h2>
                <button 
                  onClick={() => setShowFinancialModal(false)}
                  className={`${
                    darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.revenuePotentialOverview')}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className={`relative rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <div className={`flex items-center gap-2 text-sm mb-1 ${
                        darkMode ? 'text-gray-300' : 'text-slate-500'
                      }`}>
                        Annual Recurring Revenue (ARR)
                        <div 
                          className="relative"
                          onMouseEnter={() => setHoveredTooltip('arr')}
                          onMouseLeave={() => setHoveredTooltip(null)}
                        >
                          <svg className="w-4 h-4 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {hoveredTooltip === 'arr' && (
                            <div className={`absolute z-10 w-64 p-3 text-xs rounded-lg shadow-lg border bottom-full left-1/2 transform -translate-x-1/2 mb-2 ${
                              darkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-200'
                            }`}>
                              {tooltips.arr}
                              <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                darkMode ? 'border-t-gray-800' : 'border-t-white'
                              }`}></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`text-xl font-bold ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>{idea.financialData?.arr || 'N/A'}</div>
                    </div>
                    <div className={`relative rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <div className={`flex items-center gap-2 text-sm mb-1 ${
                        darkMode ? 'text-gray-300' : 'text-slate-500'
                      }`}>
                        Market Size (TAM)
                        <div 
                          className="relative"
                          onMouseEnter={() => setHoveredTooltip('tam')}
                          onMouseLeave={() => setHoveredTooltip(null)}
                        >
                          <svg className="w-4 h-4 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {hoveredTooltip === 'tam' && (
                            <div className={`absolute z-10 w-64 p-3 text-xs rounded-lg shadow-lg border bottom-full left-1/2 transform -translate-x-1/2 mb-2 ${
                              darkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-200'
                            }`}>
                              {tooltips.tam}
                              <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                darkMode ? 'border-t-gray-800' : 'border-t-white'
                              }`}></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`text-xl font-bold ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>{idea.financialData?.tam || 'N/A'}</div>
                    </div>
                    <div className={`relative rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <div className={`flex items-center gap-2 text-sm mb-1 ${
                        darkMode ? 'text-gray-300' : 'text-slate-500'
                      }`}>
                        Serviceable Market (SAM)
                        <div 
                          className="relative"
                          onMouseEnter={() => setHoveredTooltip('sam')}
                          onMouseLeave={() => setHoveredTooltip(null)}
                        >
                          <svg className="w-4 h-4 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {hoveredTooltip === 'sam' && (
                            <div className={`absolute z-10 w-64 p-3 text-xs rounded-lg shadow-lg border bottom-full left-1/2 transform -translate-x-1/2 mb-2 ${
                              darkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-200'
                            }`}>
                              {tooltips.sam}
                              <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent ${
                                darkMode ? 'border-t-gray-800' : 'border-t-white'
                              }`}></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`text-xl font-bold ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>{idea.financialData?.sam || 'N/A'}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.financialEstimations')}</h3>
                  <div className={`rounded-lg p-4 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    {idea.idea_financial_projections && idea.idea_financial_projections.length > 0 ? (
                      <div className="space-y-4">
                        {idea.idea_financial_projections.map((projection, index) => (
                          <div key={index}>
                            {projection.financial_estimation_summary && (
                              <p className={`mb-3 ${
                                darkMode ? 'text-gray-300' : 'text-slate-600'
                              }`}>{projection.financial_estimation_summary}</p>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              {projection.projected_revenue && (
                                <div>
                                  <div className={`font-medium ${
                                    darkMode ? 'text-white' : 'text-slate-700'
                                  }`}>Year {projection.year} Revenue</div>
                                  <div className={`${
                                    darkMode ? 'text-gray-300' : 'text-slate-600'
                                  }`}>{projection.projected_revenue}</div>
                                </div>
                              )}
                              {projection.projected_costs && (
                                <div>
                                  <div className={`font-medium ${
                                    darkMode ? 'text-white' : 'text-slate-700'
                                  }`}>Projected Costs</div>
                                  <div className={`${
                                    darkMode ? 'text-gray-300' : 'text-slate-600'
                                  }`}>{projection.projected_costs}</div>
                                </div>
                              )}
                              {projection.customer_acquisition_cost && (
                                <div>
                                  <div className={`font-medium ${
                                    darkMode ? 'text-white' : 'text-slate-700'
                                  }`}>CAC</div>
                                  <div className={`${
                                    darkMode ? 'text-gray-300' : 'text-slate-600'
                                  }`}>{projection.customer_acquisition_cost}</div>
                                </div>
                              )}
                              {projection.lifetime_value && (
                                <div>
                                  <div className={`font-medium ${
                                    darkMode ? 'text-white' : 'text-slate-700'
                                  }`}>LTV</div>
                                  <div className={`${
                                    darkMode ? 'text-gray-300' : 'text-slate-600'
                                  }`}>{projection.lifetime_value}</div>
                                </div>
                              )}
                            </div>
                            {projection.break_even_month && (
                              <div className={`mt-2 text-sm ${
                                darkMode ? 'text-gray-300' : 'text-slate-600'
                              }`}>
                                <span className="font-medium">Break-even: </span>
                                {projection.break_even_month}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={`${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>Based on market analysis and similar products, this idea shows strong potential for sustainable revenue growth with proper execution and market positioning.</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.suggestedPricingPlan')}</h3>
                  <div className="space-y-3">
                    {idea.idea_pricing_plans && idea.idea_pricing_plans.length > 0 ? (
                      idea.idea_pricing_plans.map((plan, index) => (
                        <div key={index} className={`flex justify-between items-center rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div>
                            <div className={`font-medium ${
                              darkMode ? 'text-white' : 'text-slate-700'
                            }`}>{plan.plan_name}</div>
                            {plan.description && (
                              <div className={`text-sm ${
                                darkMode ? 'text-gray-300' : 'text-slate-500'
                              }`}>{plan.description}</div>
                            )}
                            {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                              <div className={`text-xs mt-1 ${
                                darkMode ? 'text-gray-400' : 'text-slate-400'
                              }`}>
                                {plan.features.slice(0, 2).join(', ')}
                                {plan.features.length > 2 && '...'}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            {plan.price_monthly && (
                              <span className={`font-semibold ${
                                darkMode ? 'text-white' : 'text-slate-700'
                              }`}>
                                {plan.price_monthly.startsWith('$') ? plan.price_monthly : `$${plan.price_monthly}`}
                              </span>
                            )}
                            {plan.price_yearly && (
                              <div className={`text-xs ${
                                darkMode ? 'text-gray-400' : 'text-slate-500'
                              }`}>
                                {plan.price_yearly.startsWith('$') ? plan.price_yearly : `$${plan.price_yearly}`} yearly
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      // Fallback to default pricing plans if no data available
                      <>
                        <div className={`flex justify-between items-center rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div>
                            <div className={`font-medium ${
                              darkMode ? 'text-white' : 'text-slate-700'
                            }`}>Starter</div>
                            <div className={`text-sm ${
                              darkMode ? 'text-gray-300' : 'text-slate-500'
                            }`}>Basic features</div>
                          </div>
                          <span className={`font-semibold ${
                            darkMode ? 'text-white' : 'text-slate-700'
                          }`}>$49/month</span>
                        </div>
                        <div className={`flex justify-between items-center rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div>
                            <div className={`font-medium ${
                              darkMode ? 'text-white' : 'text-slate-700'
                            }`}>Professional</div>
                            <div className={`text-sm ${
                              darkMode ? 'text-gray-300' : 'text-slate-500'
                            }`}>Advanced features</div>
                          </div>
                          <span className={`font-semibold ${
                            darkMode ? 'text-white' : 'text-slate-700'
                          }`}>$149/month</span>
                        </div>
                        <div className={`flex justify-between items-center rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div>
                            <div className={`font-medium ${
                              darkMode ? 'text-white' : 'text-slate-700'
                            }`}>Enterprise</div>
                            <div className={`text-sm ${
                              darkMode ? 'text-gray-300' : 'text-slate-500'
                            }`}>Full features</div>
                          </div>
                          <span className={`font-semibold ${
                            darkMode ? 'text-white' : 'text-slate-700'
                          }`}>$499/month</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Execution Modal */}
      {showExecutionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowExecutionModal(false)}>
          <div className={`rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`} onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaDetail.executionStrategy')}</h2>
                <button 
                  onClick={() => setShowExecutionModal(false)}
                  className={`${
                    darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.technicalRequirements')}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>Frontend</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>{idea.technicalRequirements?.frontend || 'N/A'}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>Backend</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>{idea.technicalRequirements?.backend || 'N/A'}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>Hosting</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>{idea.technicalRequirements?.hosting || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.scalabilitySecurityConsiderations')}</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Scalability Considerations */}
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>{t('ideaDetail.scalabilityConsiderations')}</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        {isRTL && idea.technicalRequirements?.scalability_considerations_ar 
                          ? idea.technicalRequirements.scalability_considerations_ar
                          : idea.technicalRequirements?.scalability_considerations || 'No scalability considerations specified'
                        }
                      </p>
                    </div>
                    
                    {/* Security Requirements */}
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>{t('ideaDetail.securityRequirements')}</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        {isRTL && idea.technicalRequirements?.security_requirements_ar 
                          ? idea.technicalRequirements.security_requirements_ar
                          : idea.technicalRequirements?.security_requirements || 'No security requirements specified'
                        }
                      </p>
                    </div>
                    
                    {/* Third-party Integrations */}
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>{t('ideaDetail.thirdPartyIntegrations')}</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        {isRTL && idea.technicalRequirements?.third_party_integrations_ar 
                          ? idea.technicalRequirements.third_party_integrations_ar
                          : idea.technicalRequirements?.third_party_integrations || 'No third-party integrations specified'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Go-to-Market Modal */}
      {showGoToMarketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowGoToMarketModal(false)}>
          <div className={`rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`} onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaDetail.goToMarketStrategy')}</h2>
                <button 
                  onClick={() => setShowGoToMarketModal(false)}
                  className={`${
                    darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.strategyOverview')}</h3>
                  <div className={`rounded-lg p-4 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <p className={`${
                      darkMode ? 'text-gray-300' : 'text-slate-600'
                    }`}>{idea.go_to_market || idea.goToMarket || 'No strategy information available'}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.targetAudienceTactics')}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>Target Audiences</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>{idea.target_audience || idea.targetAudience}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`font-medium mb-2 ${
                        darkMode ? 'text-white' : 'text-slate-700'
                      }`}>Go-to-Market Tactics</h4>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>Content marketing, community engagement, freemium model, SEO optimization</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${
                    darkMode ? 'text-white' : 'text-slate-700'
                  } text-start`}>{t('ideaDetail.positioningAngles')}</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {idea.idea_positioning_strategies && idea.idea_positioning_strategies.length > 0 ? (
                      idea.idea_positioning_strategies.map((strategy, index) => (
                        <div key={index} className={`rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div className={`text-sm font-medium mb-1 ${
                            darkMode ? 'text-white' : 'text-slate-700'
                          }`}>{strategy.positioning_angle}</div>
                          {strategy.value_proposition && (
                            <div className={`text-xs ${
                              darkMode ? 'text-gray-300' : 'text-slate-600'
                            }`}>{strategy.value_proposition}</div>
                          )}
                          {strategy.messaging && (
                            <div className={`text-xs mt-1 italic ${
                              darkMode ? 'text-gray-400' : 'text-slate-500'
                            }`}>"{strategy.messaging}"</div>
                          )}
                        </div>
                      ))
                    ) : (
                      // Fallback positioning angles if no data available
                      <>
                        <div className={`rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div className={`text-sm ${
                            darkMode ? 'text-gray-300' : 'text-slate-600'
                          }`}>"Innovation-driven solution"</div>
                        </div>
                        <div className={`rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div className={`text-sm ${
                            darkMode ? 'text-gray-300' : 'text-slate-600'
                          }`}>"Efficiency and optimization"</div>
                        </div>
                        <div className={`rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div className={`text-sm ${
                            darkMode ? 'text-gray-300' : 'text-slate-600'
                          }`}>"User-centric design"</div>
                        </div>
                        <div className={`rounded-lg p-3 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <div className={`text-sm ${
                            darkMode ? 'text-gray-300' : 'text-slate-600'
                          }`}>"Competitive advantage"</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      ) : (
        /* Limited view for trial users who have exhausted their views */
        <div className={`text-center py-16 rounded-2xl shadow-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>🔒</div>
          <h3 className={`text-2xl font-bold mb-4 ${
            darkMode ? 'text-white' : 'text-slate-700'
          }`}>
            {i18n.language === 'ar' ? 'تحليل مفصل محدود' : 'Detailed Analysis Limited'}
          </h3>
          <p className={`mb-6 max-w-md mx-auto ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          }`}>
            {i18n.language === 'ar' 
              ? 'لقد وصلت إلى الحد الأقصى لعدد مشاهدات التحليل المفصل للأفكار. قم بالترقية للوصول الكامل إلى جميع التفاصيل.'
              : 'You have reached your limit for detailed idea analysis views. Upgrade for full access to all details.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/profile#billing')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:shadow-lg transition-all"
            >
              {i18n.language === 'ar' ? 'ترقية الحساب' : 'Upgrade to Pro'}
            </button>
            <button
              onClick={() => navigate('/ideas-vault')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                darkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {i18n.language === 'ar' ? 'تصفح المزيد من الأفكار' : 'Browse More Ideas'}
            </button>
          </div>
        </div>
      )}

      {/* Mitigation Strategies Modal */}
      {showMitigationModal && selectedCategoryRisks && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`relative max-w-4xl w-full max-h-[90vh] rounded-2xl overflow-hidden ${
            darkMode ? 'bg-gray-900' : 'bg-white'
          } shadow-2xl`}>
            {/* Modal Header */}
            <div className={`p-6 border-b ${
              darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white text-lg">🛡️</span>
                  </div>
                  <div>
                    <h2 className={`text-xl font-semibold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {isRTL ? `استراتيجيات التخفيف - ${selectedCategoryRisks.category}` : `Mitigation Strategies - ${selectedCategoryRisks.category}`}
                    </h2>
                    <p className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {isRTL ? `${selectedCategoryRisks.risks.length} مخاطر محددة` : `${selectedCategoryRisks.risks.length} identified risks`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMitigationModal(false)}
                  className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                    darkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {selectedCategoryRisks.risks.map((risk, index) => (
                  <div key={index} className={`rounded-xl p-6 border transition-all duration-300 hover:shadow-lg ${
                    darkMode ? 'bg-gray-800/50 border-gray-700 hover:border-gray-600' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}>
                    {/* Risk Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg">
                            {risk.risk_level?.toLowerCase() === 'high' ? '🔴' : 
                             risk.risk_level?.toLowerCase() === 'medium' ? '🟡' : 
                             risk.risk_level?.toLowerCase() === 'low' ? '🟢' : '⚪'}
                          </span>
                          <h3 className={`text-lg font-semibold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {isRTL ? (risk.risk_type_ar || risk.risk_type) : risk.risk_type}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r text-white ${
                            risk.risk_level?.toLowerCase() === 'high' ? 'from-red-500 to-red-600' :
                            risk.risk_level?.toLowerCase() === 'medium' ? 'from-yellow-500 to-yellow-600' :
                            risk.risk_level?.toLowerCase() === 'low' ? 'from-green-500 to-green-600' :
                            'from-gray-500 to-gray-600'
                          }`}>
                            {isRTL ? {
                              'high': 'عالي',
                              'medium': 'متوسط',
                              'low': 'منخفض'
                            }[risk.risk_level?.toLowerCase()] || risk.risk_level : risk.risk_level}
                          </span>
                        </div>
                        <p className={`text-sm leading-relaxed ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {isRTL ? (risk.risk_description_ar || risk.risk_description) : risk.risk_description}
                        </p>
                      </div>
                    </div>

                    {/* Mitigation Strategy */}
                    {risk.mitigation_strategy && (
                      <div className={`mt-4 p-4 rounded-lg border-l-4 ${
                        risk.risk_level?.toLowerCase() === 'high' ? 'border-l-red-500 bg-red-50 dark:bg-red-900/10' :
                        risk.risk_level?.toLowerCase() === 'medium' ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10' :
                        risk.risk_level?.toLowerCase() === 'low' ? 'border-l-green-500 bg-green-50 dark:bg-green-900/10' :
                        'border-l-gray-500 bg-gray-50 dark:bg-gray-900/10'
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full mt-0.5 ${
                            risk.risk_level?.toLowerCase() === 'high' ? 'bg-red-100 dark:bg-red-900/20' :
                            risk.risk_level?.toLowerCase() === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/20' :
                            risk.risk_level?.toLowerCase() === 'low' ? 'bg-green-100 dark:bg-green-900/20' :
                            'bg-gray-100 dark:bg-gray-900/20'
                          }`}>
                            <span className="text-sm">💡</span>
                          </div>
                          <div className="flex-1">
                            <h4 className={`text-sm font-semibold mb-2 ${
                              risk.risk_level?.toLowerCase() === 'high' ? 'text-red-700 dark:text-red-300' :
                              risk.risk_level?.toLowerCase() === 'medium' ? 'text-yellow-700 dark:text-yellow-300' :
                              risk.risk_level?.toLowerCase() === 'low' ? 'text-green-700 dark:text-green-300' :
                              'text-gray-700 dark:text-gray-300'
                            }`}>
                              {isRTL ? 'استراتيجية التخفيف' : 'Mitigation Strategy'}
                            </h4>
                            <p className={`text-sm leading-relaxed ${
                              risk.risk_level?.toLowerCase() === 'high' ? 'text-red-600 dark:text-red-400' :
                              risk.risk_level?.toLowerCase() === 'medium' ? 'text-yellow-600 dark:text-yellow-400' :
                              risk.risk_level?.toLowerCase() === 'low' ? 'text-green-600 dark:text-green-400' :
                              'text-gray-600 dark:text-gray-400'
                            }`}>
                              {isRTL ? (risk.mitigation_strategy_ar || risk.mitigation_strategy) : risk.mitigation_strategy}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`p-6 border-t ${
              darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowMitigationModal(false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    darkMode 
                      ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  {isRTL ? 'إغلاق' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdeaDetail;